import { storedAttribution, clearAttribution } from '../signupAttribution';
import { clearCosteoPendiente, costeoPendiente } from '../costeo';
import type { EmpiezaPor } from '@alexiarest/shared';
import { useEffect, useRef, useState } from 'react';
import { altaPendiente, datosDeAlta, EMPIEZA_POR_TEXTO, guardarPlatoContado, olvidarAlta, type AltaConAlexia } from '../altaConAlexia';
import { IconoCheck } from '../components/icons';
import { AlexiaAvatar } from '../components/AlexiaAvatar';
import { useQueryClient } from '@tanstack/react-query';
import { CURRENCIES, countryPreset, esPaisConocido, formatMoneyIn } from '@alexiarest/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { CheckboxField, SearchSelectField, TextField } from '../components/Field';
import { PhoneField } from '../components/PhoneField';
import { countryOptionsFor } from '../countryOptions';
import { useToast } from '../components/Toast';
import { founderAccessCodeFrom } from '../founderInvite';
import { storedInvitation } from '../invitation';
import { invitationOffer } from '@alexiarest/shared';
import { AltaShell } from './AltaCuenta';

/**
 * Siembra el plato de la calculadora /costeo en la cuenta recien creada:
 * ingredientes y receta de una vez (el servidor estima el precio de los que
 * llegan sin el), los precios que el usuario ajusto a mano se respetan, el
 * plato entra en la carta con su PVP y el paso de receta se da
 * por hecho; la configuración continúa. Un fallo aqui NUNCA rompe el
 * alta: la cuenta queda vacia pero funcional y el pendiente se conserva.
 * Devuelve el nombre del plato sembrado, o null.
 */
async function sembrarCosteoPendiente(founderPending: boolean): Promise<string | null> {
  const pendiente = costeoPendiente();
  if (!pendiente || founderPending) return null;
  const lines = pendiente.lines.filter((l) => l.name.trim() && l.qty > 0);
  if (!lines.length) { clearCosteoPendiente(); return null; }
  try {
    const { recipe, newIngredients } = await api.confirmRecipeProposal({
      name: pendiente.name.trim() || 'Mi plato estrella',
      servings: 1,
      lines: lines.map((l) => ({ ingredientName: l.name.trim(), createNew: true, qty: l.qty, unit: l.unit, wastePct: 0, estimated: l.netPrice == null })),
    });
    for (const l of lines) {
      if (l.netPrice == null || l.netPrice <= 0) continue;
      const creado = newIngredients.find((i) => i.name.trim().toLowerCase() === l.name.trim().toLowerCase());
      if (creado) await api.updateIngredient(creado.ingredientId, { name: creado.name, active: true, group: creado.group ?? 'otros', purchaseQty: 1, purchaseUnit: l.unit, netPrice: l.netPrice, vatPct: creado.vatPct ?? 10 });
    }
    if (pendiente.sellingPriceGross > 0) {
      await api.createMenuItem({ recipeId: recipe.recipeId, name: recipe.name, sellingPriceGross: pendiente.sellingPriceGross, vatPct: pendiente.vatPct || 10, targetFoodCostPct: 30, active: true });
    }
    clearCosteoPendiente();
    return recipe.name;
  } catch (error) {
    console.warn('[costeo] la siembra queda pendiente', error);
    return null;
  }
}

const CURRENCY_OPTIONS = Object.values(CURRENCIES).map((info) => ({
  value: info.code,
  label: info.label,
}));

/**
 * Alta del restaurante para un usuario recien registrado: tiene sesion pero
 * su token no lleva tenant. Es el paso 1 del cuestionario inicial (ver
 * SetupWizardPage): tres campos que crean el restaurante, y a partir de ahi
 * los pasos 2 a 5 salen del tenant recien creado.
 * Al terminar se refrescan los tokens (el claim nuevo no esta en el actual).
 */
export function OnboardingPage(): JSX.Element {
  const { user, logout, refreshSession } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();

  // Lo contestado en el chat de la landing (alta con Alexia), si lo hay.
  const [alta] = useState(altaPendiente);
  const [altaFallida, setAltaFallida] = useState(false);
  // Quien llega de la calculadora /costeo ya eligio con los pies: el plato.
  const [empiezaPor, setEmpiezaPor] = useState<EmpiezaPor | undefined>(() => alta?.empiezaPor ?? (costeoPendiente() ? 'plato' : undefined));
  const [name, setName] = useState(alta?.restaurante ?? '');
  const [country, setCountry] = useState(alta?.pais ?? 'ES');
  const [currency, setCurrency] = useState(alta ? countryPreset(alta.pais).currency : 'EUR');
  const [whatsapp, setWhatsapp] = useState(alta?.whatsapp ?? '');
  // Sin marcar por defecto: una casilla premarcada no vale como permiso.
  const [newsletter, setNewsletter] = useState(alta?.newsletter ?? false);
  const [saving, setSaving] = useState(false);

  const preset = countryPreset(country);

  // La invitación Founder sobrevive al login en la URL (returnTo). Con ella
  // el alta nace sin prueba y bloqueada en la página de pago.
  const founderCode = founderAccessCodeFrom(window.location.search);
  /*
   * El WhatsApp se pide a todos y es obligatorio para los Founder (documento
   * de Alex, 5 sep 2026): es el canal directo para acompañar y pedir
   * feedback en esta primera etapa, y es mejor pedirlo aquí, diciendo para
   * qué, que ir detrás de cada uno después.
   */
  const esFounder = founderCode !== '';

  // Cambiar de pais propone su moneda; lo que el usuario elija despues, manda.
  const onCountry = (value: string) => {
    if (!esPaisConocido(value)) return;
    setCountry(value);
    setCurrency(countryPreset(value).currency);
  };

  const submit = async () => {
    if (!empiezaPor) { toast.notify('Elige por dónde quieres empezar.', 'error'); return; }
    if (!name.trim()) {
      toast.notify('Ponle nombre a tu restaurante.', 'error');
      return;
    }
    if (esFounder && !whatsapp.trim()) {
      toast.notify('Déjanos tu WhatsApp: es como te acompañamos siendo Founder.', 'error');
      return;
    }
    setSaving(true);
    try {
      const result = await api.onboard({
        onboardingGoal: empiezaPor === 'plato' ? 'costs' : 'numbers',
        empiezaPor,
        persona: user?.name && !user.name.includes('@') ? user.name : undefined,
        signupCampaign: costeoPendiente() ? 'costeo-express' : undefined,
        acquisition: storedAttribution(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locale: navigator.language,
        name: name.trim(),
        country,
        currency,
        founderCode: founderCode || undefined,
        invitation: storedInvitation(),
        whatsapp: whatsapp.trim() || undefined,
        newsletter,
      });
      clearAttribution();
      // El restaurante ya existe: el alta del chat (si la había y falló) no
      // debe volver a pintarse en el login (bugbot 15 oct 2026).
      olvidarAlta();
      if (result.tokensStale) {
        // El ID token actual no lleva el claim del tenant recien creado.
        const ok = await refreshSession();
        if (!ok) {
          toast.notify(
            'Tu restaurante está creado, pero hay que entrar de nuevo para verlo.',
            'info',
          );
          logout();
          return;
        }
      }
      const sembrado = await sembrarCosteoPendiente(result.tenant.founderPending === true);
      qc.clear();
      toast.success(
        result.tenant.founderPending
          ? `¡${result.tenant.name} está en marcha! Completa el pago Founder para entrar.`
          : `¡${result.tenant.name} está en marcha! 14 días de prueba por delante.`,
      );
      if (sembrado) toast.notify(`Tu costeo de «${sembrado}» ya está en tu carta: confirma tus precios reales para afinarlo.`, 'info');
      const oferta = invitationOffer(result.tenant.invitation);
      if (oferta) {
        toast.notify(`Tu oferta ${oferta.name} está guardada: la verás al contratar en Configuración → Tu plan.`, 'info');
      }
    } catch (error) {
      toast.fail(error);
    } finally {
      setSaving(false);
    }
  };

  if (alta && !altaFallida && !founderCode) {
    return <AltaEnMarcha alta={alta} onFallo={() => setAltaFallida(true)} />;
  }

  return (
    <AltaShell una onSalir={logout}>
      <form
        className="alta__form alta__form--ancho"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <p className="alta__eyebrow">Tu restaurante</p>
        <h1 className="alta__titulo">{user?.name && !user.name.includes('@') ? `Hola, ${user.name.split(' ')[0]}. ` : ''}Empecemos por tu restaurante</h1>
        <p className="alta__texto">14 días gratis, sin tarjeta. Lo demás lo cambias cuando quieras en Configuración.</p>
        <div className="form">
          <fieldset className="goal-choices">
            <legend>¿Por dónde quieres empezar?</legend>
            <div className="goal-choices__grid">
              {(['plato', 'factura', 'ventas'] as const).map((id) => (
                <label key={id} className={`goal-choice ${empiezaPor === id ? 'is-selected' : ''}`}>
                  <input type="radio" name="empieza-por" value={id} checked={empiezaPor === id} onChange={() => setEmpiezaPor(id)} />
                  <span><strong>{EMPIEZA_POR_TEXTO[id]}</strong><small>{EMPIEZA_DETALLE[id]}</small></span>
                </label>
              ))}
            </div>
          </fieldset>
          <TextField
            label="Nombre del restaurante"
            required
            value={name}
            onChange={setName}
            placeholder="Casa Mireia"
          />
          <SearchSelectField
            label="País"
            value={country}
            onChange={onCountry}
            options={countryOptionsFor(country)}
            placeholder="Busca por nombre o código…"
          />
          <SearchSelectField
            label="Moneda"
            value={currency}
            onChange={setCurrency}
            options={CURRENCY_OPTIONS}
            placeholder="Busca por nombre, símbolo o código…"
            hint={
              preset.generico
                ? `Ejemplo: ${formatMoneyIn(1234.56, currency)} · El impuesto de tus ventas lo pones al entrar, en «Para empezar».`
                : `Ejemplo: ${formatMoneyIn(1234.56, currency)} · Te dejo puesto el ${preset.taxName} de ${preset.label}: ${preset.vatPct} %.`
            }
          />
          {esFounder && (
            <PhoneField
              label="WhatsApp de contacto"
              required
              value={whatsapp}
              onChange={setWhatsapp}
              defaultCountry={country}
              hint="Como Founder tendrás contacto directo con nosotros por WhatsApp."
            />
          )}
          <CheckboxField
            label="Quiero recibir la newsletter y ofertas de Alexia. Los avisos de mi cuenta me llegan igualmente."
            checked={newsletter}
            onChange={setNewsletter}
          />
        </div>
        <button type="submit" className="btn alta__enviar" disabled={saving}>
          {saving ? 'Creando tu restaurante…' : 'Entrar a Alexia →'}
        </button>
      </form>
    </AltaShell>
  );
}

const EMPIEZA_DETALLE: Record<EmpiezaPor, string> = {
  plato: 'Dicta tu plato estrella y sabes cuánto te deja en cada canal.',
  factura: 'Con una foto, Alexia guarda el gasto y los precios de tus ingredientes.',
  ventas: 'Conecta tu punto de venta o sube un Excel y ves cómo va tu mes.',
};

/**
 * Alta con Alexia (13 oct 2026; sin cuestionario desde el 15 oct 2026): quien
 * contestó en el chat de la landing no ve ningún formulario. Al crear la
 * cuenta se crea el restaurante con lo que contó; la API pone lo demás según
 * el país (moneda, impuesto, medidas) y las apps con su comisión habitual
 * marcada para confirmar. Después, el Panel del primer día con lo que eligió
 * ya abierto en el lateral. Si el alta falla, el formulario de siempre
 * aparece relleno.
 */
function AltaEnMarcha({ alta, onFallo }: { alta: AltaConAlexia; onFallo: () => void }): JSX.Element {
  const { logout, refreshSession } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const arrancado = useRef(false);
  const preset = countryPreset(alta.pais);
  const datos = datosDeAlta(alta);
  const pasos = [
    `Creando ${alta.restaurante}`,
    preset.generico
      ? `Poniendo ${datos.currency} y la hora de ${preset.label}`
      : `Poniendo ${datos.currency}, el ${preset.taxName} del ${preset.vatPct} % y la hora de ${preset.label}`,
    alta.delivery.length ? `Agregando ${alta.delivery.join(', ')} con su comisión de siempre` : 'Dejando listas tus ventas en sala',
    'Preparando tu Panel del primer día',
  ];
  const [hechos, setHechos] = useState(0);

  useEffect(() => {
    // StrictMode monta dos veces: el alta no puede lanzarse dos.
    if (arrancado.current) return;
    arrancado.current = true;
    const marcar = (n: number) => setHechos((h) => Math.max(h, n));
    void (async () => {
      let result;
      try {
        result = await api.onboard({
          ...datos,
          signupCampaign: costeoPendiente() ? 'costeo-express' : 'alta-con-alexia',
          acquisition: storedAttribution(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale: navigator.language,
          invitation: storedInvitation(),
        });
      } catch (error) {
        toast.fail(error);
        onFallo();
        return;
      }
      clearAttribution();
      marcar(1);
      if (result.tokensStale && !(await refreshSession())) {
        toast.notify('Tu restaurante está creado, pero hay que entrar de nuevo para verlo.', 'info');
        olvidarAlta();
        logout();
        return;
      }
      marcar(3);
      const sembrado = await sembrarCosteoPendiente(result.tenant.founderPending === true);
      if (alta.plato) guardarPlatoContado(alta.plato);
      olvidarAlta();
      marcar(4);
      // Un instante para que se vea la última marca antes de cambiar de pantalla.
      await new Promise((r) => window.setTimeout(r, 700));
      qc.clear();
      if (sembrado) toast.notify(`Tu costeo de «${sembrado}» ya está en tu carta.`, 'info');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- una sola vez
  }, []);

  return (
    <AltaShell una>
      <section className="alta__preparando" aria-live="polite">
        <span className="alta__avatar" aria-hidden="true"><AlexiaAvatar estado={hechos >= pasos.length ? 'hecho' : 'pensando'} /></span>
        <h1 className="alta__titulo">
          {alta.persona ? `Gracias, ${alta.persona}. ` : ''}Estoy preparando {alta.restaurante}
        </h1>
        <p className="alta__texto">Con lo que me contaste. Tardo unos segundos.</p>
        <ol className="alta__pasos">
          {pasos.map((paso, i) => {
            const estado = i < hechos ? 'hecho' : i === hechos ? 'ahora' : 'luego';
            return (
              <li key={paso} className={`alta__paso is-${estado}`}>
                <span className="alta__marca" aria-hidden="true">{estado === 'hecho' ? <IconoCheck /> : null}</span>
                {paso}
              </li>
            );
          })}
        </ol>
      </section>
    </AltaShell>
  );
}
